import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Utensils,
  Eye,
  EyeOff,
  Check,
  X,
} from "lucide-react";
import { colors } from "../../../constants/colors";

const DietPreferencesSection = ({ uid }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dietPreferencesData, setDietPreferencesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDietPreferences = async () => {
    setLoading(true);
    setError(null);
    try {
      let token = sessionStorage.getItem("adminToken");
      if (!token) {
        token = localStorage.getItem("authToken");
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/users/${uid}/diet-preferences`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch diet preferences");
      }

      const data = await response.json();
      setDietPreferencesData(data.dietPreferences);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching diet preferences:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isExpanded && !dietPreferencesData) {
      fetchDietPreferences();
    }
    setIsExpanded(!isExpanded);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "Not set";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(", ") : "None";
      }
      return JSON.stringify(value, null, 2);
    }
    return value.toString();
  };

  const renderBooleanValue = (value) => {
    if (value === true) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#059669",
          }}
        >
          <Check size={16} />
          Yes
        </div>
      );
    } else if (value === false) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#dc2626",
          }}
        >
          <X size={16} />
          No
        </div>
      );
    }
    return <span style={{ color: colors.darkGray }}>Not set</span>;
  };

  const renderArrayValue = (value) => {
    if (!Array.isArray(value) || value.length === 0) {
      return <span style={{ color: colors.darkGray }}>None specified</span>;
    }
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
        {value.map((item, index) => (
          <span
            key={index}
            style={{
              backgroundColor: colors.lightGreen,
              color: colors.darkGreen,
              padding: "0.25rem 0.5rem",
              borderRadius: "12px",
              fontSize: "0.875rem",
              fontWeight: "500",
            }}
          >
            {item}
          </span>
        ))}
      </div>
    );
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "0.5rem 0",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const detailCardStyle = {
    backgroundColor: colors.background,
    borderRadius: "8px",
    padding: "1rem",
    marginBottom: "1rem",
    border: `1px solid ${colors.lightGray}`,
  };

  const labelStyle = {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: colors.darkGray,
    marginBottom: "0.5rem",
    textTransform: "capitalize",
  };

  const valueStyle = {
    fontSize: "1rem",
    color: colors.text.primary,
    lineHeight: "1.5",
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={handleToggle}>
        <div style={titleStyle}>
          <Utensils size={20} />
          Diet Preferences
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {isExpanded ? (
            <EyeOff size={16} color={colors.darkGray} />
          ) : (
            <Eye size={16} color={colors.darkGray} />
          )}
          {isExpanded ? (
            <ChevronUp size={20} color={colors.darkGray} />
          ) : (
            <ChevronDown size={20} color={colors.darkGray} />
          )}
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginTop: "1rem" }}>
          {/* Loading State */}
          {loading && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: colors.darkGray,
              }}
            >
              Loading diet preferences...
            </div>
          )}

          {/* Error State */}
          {error && (
            <div
              style={{
                backgroundColor: "#fee",
                color: "#c33",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                border: "1px solid #fcc",
              }}
            >
              Error: {error}
            </div>
          )}

          {/* Diet Preferences Data */}
          {dietPreferencesData && !loading && (
            <div>
              <div
                style={{
                  marginBottom: "1.5rem",
                  padding: "1rem",
                  backgroundColor: colors.lightGreen,
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: colors.darkGreen,
                    fontSize: "1.1rem",
                    fontWeight: "600",
                  }}
                >
                  Dietary Preferences & Restrictions
                </h3>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1rem",
                }}
              >
                {Object.entries(dietPreferencesData).map(([key, value]) => (
                  <div key={key} style={detailCardStyle}>
                    <div style={labelStyle}>
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </div>
                    <div style={valueStyle}>
                      {typeof value === "boolean" ? (
                        renderBooleanValue(value)
                      ) : Array.isArray(value) ? (
                        renderArrayValue(value)
                      ) : typeof value === "object" && value !== null ? (
                        <pre
                          style={{
                            fontSize: "0.875rem",
                            backgroundColor: "#f8f9fa",
                            padding: "0.5rem",
                            borderRadius: "4px",
                            overflow: "auto",
                            margin: 0,
                            fontFamily: "monospace",
                          }}
                        >
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      ) : (
                        <span>{formatValue(value)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {Object.keys(dietPreferencesData).length === 0 && (
                <div
                  style={{
                    marginTop: "1.5rem",
                    padding: "2rem",
                    textAlign: "center",
                    color: colors.darkGray,
                    backgroundColor: colors.lightGray,
                    borderRadius: "8px",
                  }}
                >
                  <Utensils
                    size={32}
                    color={colors.darkGray}
                    style={{ marginBottom: "0.5rem" }}
                  />
                  <div>No diet preferences data available</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DietPreferencesSection;
